/**
 * Disbursement review queue (BE-3, VATP013)
 *
 * Route: /accounting/disbursement-reviews
 * Evidence given as VERBAL customer approval (with its uploaded receipt) waits here until finance views the receipt and
 * accepts or rejects it. A work order cannot complete (and a direct invoice cannot finalise) while a TC-paid
 * pass-through's evidence is not ACCEPTED.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Paper, Typography, Button, Alert, Snackbar, Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  CircularProgress, IconButton, Tooltip, Link, Card, CardContent, Stack, InputAdornment, MenuItem, useTheme, useMediaQuery,
} from '@mui/material';
import {
  CheckCircle as AcceptIcon, Cancel as RejectIcon, Refresh as RefreshIcon, Search as SearchIcon, Visibility as ViewIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import { SummaryCardGrid, DataTable, StatusChip } from '../../components/accounting';
import { fontSize as fs, fontWeight as fw } from '../../utils/accounting/styles';
import { can } from '../../utils/roleUtils';
import { workOrderMoneyService } from '../../services/accounting/workOrderMoney.service';
import { serverMessage } from '../../utils/apiConvert';

const KIND_LABEL = { LINE: 'Government fee', ADDON: 'Add-on', INVOICE_LINE: 'Direct invoice fee' };
const AUTH_LABEL = { SIGNED_SERVICE_ORDER: 'Signed service order', CUSTOMER_APPROVAL: 'Customer approval', VERBAL: 'Verbal' };

const sessionUser = () => {
  try { return JSON.parse(localStorage.getItem('currentUser') || '{}'); } catch (e) { return {}; }
};

const DisbursementReviews = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const canWrite = can(sessionUser(), 'finance', 'write');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState(null);
  const [kind, setKind] = useState('');
  const [search, setSearch] = useState('');
  const [dialog, setDialog] = useState(null); // { action: 'accept'|'reject', row }
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRows(await workOrderMoneyService.getReviews());
    } catch (err) {
      setError(serverMessage(err, 'Failed to load the review queue'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const confirm = async () => {
    const { action, row } = dialog;
    setBusy(true);
    setError(null);
    try {
      if (action === 'accept') await workOrderMoneyService.acceptReview(row.kind, row.id, note.trim());
      else await workOrderMoneyService.rejectReview(row.kind, row.id, note.trim());
      setSnackbar(action === 'accept' ? 'Evidence accepted' : 'Evidence rejected — the agent must provide new evidence');
      setDialog(null);
      await load();
    } catch (err) {
      setError(serverMessage(err, 'The review failed'));
      setDialog(null);
    } finally {
      setBusy(false);
    }
  };

  const openSource = (r) => {
    if (r.workOrderId) navigate(`/work-orders/${r.workOrderId}`);
    else if (r.invoiceId) navigate(`/invoices/${r.invoiceId}`);
  };
  const openDialog = (action, row) => { setNote(''); setDialog({ action, row }); };

  const term = search.trim().toLowerCase();
  const shown = rows.filter((r) => (!kind || r.kind === kind)
    && (!term || (r.receiptRef || '').toLowerCase().includes(term)));
  const count = (k) => rows.filter((r) => r.kind === k).length;

  const summaryCards = [
    { label: 'Waiting for Review', value: rows.length, suffix: rows.length === 1 ? 'item' : 'items', color: 'warning' },
    { label: 'Government Fees', value: count('LINE'), suffix: count('LINE') === 1 ? 'item' : 'items', color: 'primary' },
    { label: 'Add-ons', value: count('ADDON'), suffix: count('ADDON') === 1 ? 'item' : 'items', color: 'info' },
    { label: 'Direct Invoice Fees', value: count('INVOICE_LINE'), suffix: count('INVOICE_LINE') === 1 ? 'item' : 'items', color: 'secondary' },
  ];

  const viewReceipt = async (r) => {
    try {
      const u = await workOrderMoneyService.reviewReceiptUrl(r.kind, r.id);
      if (u?.downloadUrl) window.open(u.downloadUrl, '_blank', 'noopener');
    } catch (err) {
      setError(serverMessage(err, 'Could not open the receipt'));
    }
  };

  const actions = (r) => (
    <Box display="flex" justifyContent="flex-end" gap={0.5}>
      {r.hasReceipt && (
        <Tooltip title="View receipt"><IconButton size="small" onClick={(e) => { e.stopPropagation(); viewReceipt(r); }}><ViewIcon sx={{ fontSize: 20 }} /></IconButton></Tooltip>
      )}
      {canWrite && <><Tooltip title="Accept evidence"><IconButton size="small" color="success" onClick={(e) => { e.stopPropagation(); openDialog('accept', r); }}><AcceptIcon sx={{ fontSize: 20 }} /></IconButton></Tooltip>
      <Tooltip title="Reject evidence"><IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); openDialog('reject', r); }}><RejectIcon sx={{ fontSize: 20 }} /></IconButton></Tooltip></>}
    </Box>
  );

  const COLUMNS = [
    { key: 'kind', label: 'Item', width: '20%', render: (r) => (
      <Typography sx={{ fontSize: fs.xs, fontWeight: fw.semibold }}>{KIND_LABEL[r.kind] || r.kind}</Typography>
    ) },
    { key: 'source', label: 'Source', width: '18%', render: (r) => (
      <Link component="button" underline="hover" sx={{ fontSize: fs.xs }} onClick={() => openSource(r)}>
        {r.workOrderId ? 'Open work order' : 'Open invoice'}
      </Link>
    ) },
    { key: 'authType', label: 'Authorisation', width: '20%', render: (r) => AUTH_LABEL[r.authType] || r.authType },
    { key: 'receiptRef', label: 'Receipt Ref', width: '18%', render: (r) => r.receiptRef || '—' },
    { key: 'status', label: 'Status', width: '14%', render: () => <StatusChip tone="warning" label="Pending review" /> },
    { key: 'actions', label: '', width: '10%', align: 'right', render: actions },
  ];

  return (
    <Box sx={{ width: 0, minWidth: '100%' }}>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h5" fontWeight={600} color="secondary.main">Disbursement Review</Typography>
        <Tooltip title="Refresh"><IconButton size="small" onClick={load}><RefreshIcon fontSize="small" /></IconButton></Tooltip>
      </Box>

      <SummaryCardGrid cards={summaryCards} loading={loading} sx={{ mb: 2 }} />

      <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
        <TextField size="small" placeholder="Search receipt reference..." value={search} sx={{ flex: '1 1 240px', maxWidth: 360 }}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
        <TextField select size="small" label="Item" value={kind} sx={{ minWidth: 200 }} onChange={(e) => setKind(e.target.value)}>
          <MenuItem value="">All items</MenuItem>
          {Object.entries(KIND_LABEL).map(([v, l]) => <MenuItem key={v} value={v}>{l}</MenuItem>)}
        </TextField>
      </Box>

      {isMobile ? (
        <Stack spacing={1.5}>
          {loading ? (
            <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
          ) : shown.length === 0 ? (
            <Paper variant="outlined" sx={{ py: 4, textAlign: 'center' }}>
              <Typography color="text.secondary" sx={{ fontSize: fs.sm }}>Nothing waiting for review</Typography>
            </Paper>
          ) : shown.map((r) => (
            <Card key={`${r.kind}-${r.id}`} variant="outlined" sx={{ cursor: 'pointer', '&:hover': { boxShadow: 2 } }} onClick={() => openSource(r)}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                  <Box>
                    <Typography fontWeight={600} color="primary" sx={{ fontSize: '0.95rem' }}>{KIND_LABEL[r.kind] || r.kind}</Typography>
                    <Typography color="text.secondary" sx={{ fontSize: '0.8rem' }}>{AUTH_LABEL[r.authType] || r.authType}</Typography>
                  </Box>
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <StatusChip tone="warning" label="Pending review" />
                    <ChevronRightIcon color="action" sx={{ fontSize: 20 }} />
                  </Box>
                </Box>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="caption" color="text.secondary">Receipt ref: {r.receiptRef || '—'}</Typography>
                  {actions(r)}
                </Box>
              </CardContent>
            </Card>
          ))}
        </Stack>
      ) : (
        <DataTable columns={COLUMNS} data={shown} loading={loading} emptyMessage="Nothing waiting for review" />
      )}

      <Dialog open={!!dialog} onClose={() => setDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{dialog?.action === 'accept' ? 'Accept Evidence' : 'Reject Evidence'}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {dialog?.action === 'accept'
              ? 'The item counts as properly evidenced; the work order can complete.'
              : 'The agent has to record new evidence before the work order can complete.'}
          </Typography>
          <TextField label="Note (optional)" fullWidth size="small" multiline rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(null)}>Cancel</Button>
          <Button variant="contained" color={dialog?.action === 'accept' ? 'success' : 'error'} disabled={busy} onClick={confirm}>
            {dialog?.action === 'accept' ? 'Accept' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!snackbar} autoHideDuration={3500} onClose={() => setSnackbar(null)} message={snackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
    </Box>
  );
};

export default DisbursementReviews;
